// Sort orders are stored as "<column>Asc" / "<column>Desc" (the same strings
// the old UI stored, so a returning user keeps their sort).

export type SortDirection = 'Asc' | 'Desc';

export interface ParsedSort {
    column: string;
    direction: SortDirection;
}

export function parseSortOrder(order: string): ParsedSort {
    return order.endsWith('Asc')
        ? { column: order.slice(0, -3), direction: 'Asc' }
        : { column: order.replace(/Desc$/, ''), direction: 'Desc' };
}

// Tapping a column header: a new column sorts descending, the current column flips.
export function nextSortOrder(current: string, column: string): string {
    const parsed = parseSortOrder(current);
    if (parsed.column === column) {
        return `${column}${parsed.direction === 'Desc' ? 'Asc' : 'Desc'}`;
    }
    return `${column}Desc`;
}

const compareValues = (a: unknown, b: unknown): number => {
    if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b, 'fi');
    }
    return (a as number) - (b as number);
};

// Sorts rows by the value a column extractor returns; rows with no value go last
// whichever way the order points. `tieBreak` orders equal values.
export function sortRows<T>(
    rows: readonly T[],
    order: string,
    columns: Readonly<Record<string, (row: T) => number | string | undefined>>,
    tieBreak: (a: T, b: T) => number,
): T[] {
    const { column, direction } = parseSortOrder(order);
    const value = columns[column];
    if (value === undefined) {
        return [...rows].sort(tieBreak);
    }
    const sign = direction === 'Desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const left = value(a);
        const right = value(b);
        if (left === undefined || right === undefined) {
            return left === right ? tieBreak(a, b) : (left === undefined ? 1 : -1);
        }
        return sign * compareValues(left, right) || tieBreak(a, b);
    });
}
